import { useState, useRef, useEffect } from "react";
import { useThemeStore, themes, type ThemeName } from "../../stores/themeStore";

interface ThemeSelectorProps {
  variant?: "compact" | "full";
  isCollapsed?: boolean;
}

export function ThemeSelector({ variant = "full", isCollapsed = false }: ThemeSelectorProps) {
  const { theme, setTheme } = useThemeStore();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const currentTheme = themes.find((t) => t.name === theme) || themes[0];

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Close on escape key
  useEffect(() => {
    function handleEscape(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    }

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, []);

  const handleThemeChange = (themeName: ThemeName) => {
    setTheme(themeName);
    setIsOpen(false);
  };

  return (
    <div ref={dropdownRef} className="relative">
      {/* Theme Toggle Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`
          flex items-center gap-2 rounded-xl transition-all duration-200
          bg-surface-800/50 hover:bg-surface-700/50 border border-surface-700/30 hover:border-surface-600/50
          ${isCollapsed ? "p-2.5 justify-center" : "py-2.5 px-3 w-full"}
        `}
        title={isCollapsed ? `Theme: ${currentTheme.label}` : undefined}
        aria-label="Select theme"
        aria-expanded={isOpen}
      >
        {/* Theme preview circle */}
        <div 
          className="w-5 h-5 rounded-full flex-shrink-0 border-2 border-surface-600/50 overflow-hidden"
          style={{ 
            background: `linear-gradient(135deg, ${currentTheme.preview.primary} 0%, ${currentTheme.preview.accent} 100%)`
          }}
        />
        
        {!isCollapsed && (
          <>
            <div className="flex-1 text-left">
              <span className="text-sm font-medium text-surface-200">{currentTheme.label}</span>
            </div>
            <svg
              className={`w-4 h-4 text-surface-400 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </>
        )}
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          className={`
            absolute z-50 mt-2 py-2 rounded-xl shadow-2xl border border-surface-700/50
            bg-surface-900/95 backdrop-blur-xl
            animate-fade-in overflow-hidden
            ${isCollapsed ? "left-full ml-2 top-0 w-64" : "left-0 right-0 w-full min-w-[200px]"}
            ${variant === "compact" ? "bottom-full mb-2 top-auto" : ""}
          `}
          role="menu"
        >
          <div className="px-3 py-2 border-b border-surface-700/50">
            <h4 className="text-xs font-semibold text-surface-400 uppercase tracking-wider">
              Choose Theme
            </h4>
          </div>
          
          <div className="p-2 space-y-1 max-h-[300px] overflow-y-auto custom-scrollbar">
            {themes.map((t) => (
              <button
                key={t.name}
                onClick={() => handleThemeChange(t.name)}
                className={`
                  w-full flex items-center gap-3 p-2.5 rounded-lg transition-all duration-200
                  ${theme === t.name 
                    ? "bg-primary-500/20 border border-primary-500/30" 
                    : "hover:bg-surface-800/50 border border-transparent hover:border-surface-700/30"
                  }
                `}
                role="menuitem"
                aria-current={theme === t.name ? "true" : "false"}
              >
                {/* Theme preview */}
                <div className="relative flex-shrink-0">
                  <div 
                    className="w-10 h-10 rounded-lg border border-surface-600/30 overflow-hidden shadow-inner"
                    style={{ backgroundColor: t.preview.bg }}
                  >
                    {/* Mini card preview */}
                    <div 
                      className="absolute bottom-1 left-1 right-1 h-4 rounded-sm"
                      style={{ backgroundColor: t.preview.card }}
                    />
                    {/* Primary color dot */}
                    <div 
                      className="absolute top-1.5 left-1.5 w-2 h-2 rounded-full"
                      style={{ backgroundColor: t.preview.primary }}
                    />
                    {/* Accent color dot */}
                    <div 
                      className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full"
                      style={{ backgroundColor: t.preview.accent }}
                    />
                  </div>
                  
                  {/* Checkmark for selected theme */}
                  {theme === t.name && (
                    <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full bg-primary-500 flex items-center justify-center">
                      <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  )}
                </div>
                
                {/* Theme info */}
                <div className="flex-1 text-left min-w-0">
                  <div className="flex items-center gap-2">
                    <span className={`font-medium text-sm ${theme === t.name ? "text-primary-400" : "text-surface-200"}`}>
                      {t.label}
                    </span>
                    {t.name === "arctic" && (
                      <span className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-surface-700/50 text-surface-400">
                        LIGHT
                      </span>
                    )}
                  </div>
                  <span className="text-xs text-surface-500 truncate block">
                    {t.description}
                  </span>
                </div>
              </button>
            ))}
          </div>
          
          {/* Footer */}
          <div className="px-3 py-2 border-t border-surface-700/50">
            <p className="text-[10px] text-surface-500 text-center">
              Theme is saved automatically
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
